import { useEffect, useRef } from 'react';

/**
 * Poll while the tab is visible, and stop while it is not.
 *
 * Seventeen hand-rolled `setInterval` pollers existed, at nine different intervals, and only two of
 * them checked `document.visibilityState`. The rest kept firing in a backgrounded tab forever - a
 * forgotten `/status` tab hit the API every four seconds indefinitely. On a platform whose AWS
 * run-rate is watched daily, that is a cost line, not a nicety.
 *
 * Three guarantees the copies did not consistently have:
 *  - it pauses when the tab is hidden, and fetches once immediately on becoming visible again, so the
 *    view is never stale-but-silent;
 *  - the in-flight result is dropped after unmount, so a slow response cannot set state on a dead
 *    component or overwrite a newer view;
 *  - the callback is read from a ref, so passing an inline arrow does not restart the timer on every
 *    render.
 *
 * This is not a data-fetching library and does not pretend to be one: no cache, no dedupe, no
 * sharing between components. It solves the one problem that was actually costing money.
 */
export function usePoll(
  fn: () => void | Promise<void>,
  intervalMs: number,
  opts: { enabled?: boolean; immediate?: boolean } = {},
): void {
  const { enabled = true, immediate = true } = opts;
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    if (!enabled || intervalMs <= 0) return;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;

    const run = () => {
      if (cancelled) return;
      // A hidden tab must not poll. Checked at fire time as well as on the visibility event, because
      // an interval scheduled while visible would otherwise fire once after the tab is hidden.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      void fnRef.current();
    };

    const start = () => {
      if (timer !== undefined) return;
      timer = setInterval(run, intervalMs);
    };
    const stop = () => {
      if (timer === undefined) return;
      clearInterval(timer);
      timer = undefined;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        stop();
        return;
      }
      // Catch up immediately rather than waiting a full interval for the first refresh.
      run();
      start();
    };

    if (immediate) run();
    if (typeof document === 'undefined' || document.visibilityState === 'visible') start();
    document?.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      stop();
      document?.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, intervalMs, immediate]);
}
